import React, { useState, useEffect, useContext } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { FaBars, FaSignOutAlt, FaUser, FaMoon, FaSun } from 'react-icons/fa';
import { AuthContext } from './AuthContext';

const DashboardLayout = ({ 
  children, 
  title, 
  sidebarLinks, 
  onLogout,
  // New customization props with sensible defaults
  contentWidth = 'full',        // 'full' | 'wide' | 'normal' | 'narrow'
  padding = 'default',           // 'none' | 'small' | 'default' | 'large'
  maxWidth = null,               // Custom max-width or null for none
  containerClass = '',           // Additional classes for main container
  noPadding = false,             // Quick way to remove all padding (backward compat)
  showTopBar = true,             // Show/hide top bar
  stickyTopBar = false,          // Make top bar sticky
  backgroundColor = 'bg-gray-100', // Main background color
  showProfile = true,            // Show profile link (hidden for SuperAdmin)
  profilePath = null             // Custom profile path
}) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [fullScreenMode, setFullScreenMode] = useState(false);
  const [sidebarHidden, setSidebarHidden] = useState(false);
  const [darkMode, setDarkMode] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const authContext = useContext(AuthContext);
  
  // Check if user is SuperAdmin
  const isSuperAdmin = authContext?.user?.role === 'SuperAdmin' || 
                       authContext?.user?.user?.role?.name === 'SuperAdmin';

  // Load dark mode preference from localStorage
  useEffect(() => {
    const savedDarkMode = localStorage.getItem('darkMode') === 'true';
    setDarkMode(savedDarkMode);
    if (savedDarkMode) {
      document.documentElement.classList.add('dark');
    }
  }, []);

  // Sync dark mode with localStorage across tabs
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'darkMode') {
        const newDarkMode = e.newValue === 'true';
        setDarkMode(newDarkMode);
        if (newDarkMode) {
          document.documentElement.classList.add('dark');
        } else {
          document.documentElement.classList.remove('dark');
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Toggle dark mode
  const toggleDarkMode = () => {
    const newDarkMode = !darkMode;
    setDarkMode(newDarkMode);
    localStorage.setItem('darkMode', newDarkMode.toString());
    
    if (newDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  };

  // Content width configurations
  const widthClasses = {
    full: 'w-full',
    wide: 'max-w-[1920px] mx-auto',
    normal: 'max-w-7xl mx-auto',
    narrow: 'max-w-4xl mx-auto'
  };

  // Padding configurations
  const paddingClasses = {
    none: '',
    small: 'p-2 md:p-4',
    default: 'p-4 md:p-6',
    large: 'p-6 md:p-8 lg:p-10'
  };

  // Determine final padding class
  const finalPadding = noPadding ? '' : paddingClasses[padding];
  
  // Determine content width
  const contentWidthClass = maxWidth ? '' : widthClasses[contentWidth];
  const contentStyle = maxWidth ? { maxWidth } : {};

  // Keyboard shortcuts and event listeners
  React.useEffect(() => {
    const handleLogoutShortcut = (event) => {
      if (event.ctrlKey && event.key === 'l') {
        event.preventDefault();
        onLogout();
      }
    };

    const handleProfileShortcut = (event) => {
      if (event.ctrlKey && event.shiftKey && event.key === 'P' && showProfile && !isSuperAdmin) {
        event.preventDefault();
        navigate(profilePath || "/employee/profile");
      }
    };

    const handleDarkModeShortcut = (event) => {
      if (event.ctrlKey && event.shiftKey && event.key === 'M') {
        event.preventDefault();
        toggleDarkMode();
      }
    };

    const handleF11KeyPress = (event) => {
      if (event.key === 'F11') {
        event.preventDefault();
        setFullScreenMode(!fullScreenMode);
      }
    };

    const shortcutMap = new Map();
    sidebarLinks.forEach(link => {
      if (link.shortcut) {
        const combo = link.shortcut.toLowerCase();
        if (!shortcutMap.has(combo)) {
          shortcutMap.set(combo, () => navigate(link.to));
        } else {
          console.warn(`Duplicate shortcut: ${combo}`);
        }
      }
    });

    const handleShortcuts = (e) => {
      let combo = '';
      if (e.ctrlKey) combo += 'ctrl+';
      if (e.shiftKey) combo += 'shift+';
      if (e.altKey) combo += 'alt+';
      combo += e.key.toLowerCase();

      if (shortcutMap.has(combo)) {
        e.preventDefault();
        shortcutMap.get(combo)();
      }
    };

    document.addEventListener('keydown', handleLogoutShortcut);
    document.addEventListener('keydown', handleProfileShortcut);
    document.addEventListener('keydown', handleDarkModeShortcut);
    document.addEventListener('keydown', handleF11KeyPress);
    document.addEventListener('keydown', handleShortcuts);

    return () => {
      document.removeEventListener('keydown', handleLogoutShortcut);
      document.removeEventListener('keydown', handleProfileShortcut);
      document.removeEventListener('keydown', handleDarkModeShortcut);
      document.removeEventListener('keydown', handleF11KeyPress);
      document.removeEventListener('keydown', handleShortcuts);
    };
  }, [sidebarLinks, navigate, onLogout, fullScreenMode, sidebarHidden, darkMode, showProfile, isSuperAdmin, profilePath, toggleDarkMode]);
  
  React.useEffect(() => {
    // Function to toggle full screen mode
    const toggleFullScreen = () => {
      if (fullScreenMode) {
        // Enter full screen
        if (!document.fullscreenElement) {
          document.documentElement.requestFullscreen().catch((err) => {
            console.error(`Error attempting to enable full-screen mode: ${err.message}`);
          });
        }
      } else {
        // Exit full screen
        if (document.fullscreenElement) {
          document.exitFullscreen().catch((err) => {
            console.error(`Error attempting to exit full-screen mode: ${err.message}`);
          });
        }
      }
    };

    toggleFullScreen();

    // Listen for fullscreen changes (e.g., when user presses F11 or ESC)
    const handleFullscreenChange = () => {
      const isCurrentlyFullscreen = !!document.fullscreenElement;
      if (isCurrentlyFullscreen !== fullScreenMode) {
        setFullScreenMode(isCurrentlyFullscreen);
      }
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, [fullScreenMode]);
    // Remove any duplicate links
  sidebarLinks = sidebarLinks.filter(
    (link, index) => sidebarLinks.indexOf(link) === index
  );  return (
    <div className={`min-h-screen transition-all duration-500 ${
      darkMode 
        ? 'dark bg-gradient-to-br from-slate-950 via-blue-950 to-slate-950' 
        : 'bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50'
    }`}>
      {/* Enhanced styling with better scrollbar and smooth transitions */}
      <style>{`
        * { font-weight: 500; }
        
        /* Dark mode transitions */
        * {
          transition: background-color 0.3s ease, color 0.3s ease, border-color 0.3s ease;
        }
        
        /* Custom scrollbar styles for sidebar */
        .sidebar-scrollbar::-webkit-scrollbar {
          width: 8px;
        }
        
        .dark .sidebar-scrollbar::-webkit-scrollbar-track {
          background: #0f172a;
          border-radius: 4px;
        }
        
        .sidebar-scrollbar::-webkit-scrollbar-track {
          background: #1e3a8a;
          border-radius: 4px;
        }
        
        .dark .sidebar-scrollbar::-webkit-scrollbar-thumb {
          background: #1e40af;
          border-radius: 4px;
          border: 1px solid #0f172a;
        }
        
        .sidebar-scrollbar::-webkit-scrollbar-thumb {
          background: #2563eb;
          border-radius: 4px;
          border: 1px solid #1e3a8a;
        }
        
        .dark .sidebar-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #2563eb;
        }
        
        .sidebar-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #3b82f6;
        }
        
        .dark .sidebar-scrollbar::-webkit-scrollbar-thumb:active {
          background: #1d4ed8;
        }
        
        .sidebar-scrollbar::-webkit-scrollbar-thumb:active {
          background: #1d4ed8;
        }
        
        /* Firefox scrollbar */
        .dark .sidebar-scrollbar {
          scrollbar-width: thin;
          scrollbar-color: #1e40af #0f172a;
        }
        
        .sidebar-scrollbar {
          scrollbar-width: thin;
          scrollbar-color: #2563eb #1e3a8a;
        }

        /* Smooth transitions for all layout changes */
        .layout-transition {
          transition: margin-left 0.2s ease-in-out, width 0.2s ease-in-out;
        }

        /* Enhanced link active states */
        .sidebar-link-active {
          position: relative;
          box-shadow: 0 0 20px rgba(59, 130, 246, 0.3);
        }
        
        .dark .sidebar-link-active {
          box-shadow: 0 0 20px rgba(96, 165, 250, 0.2);
        }
        
        .sidebar-link-active::before {
          content: '';
          position: absolute;
          left: 0;
          top: 0;
          bottom: 0;
          width: 4px;
          background: linear-gradient(180deg, #3b82f6, #60a5fa);
          border-radius: 0 4px 4px 0;
          box-shadow: 0 0 8px rgba(59, 130, 246, 0.5);
        }

        /* Enhanced focus states for accessibility */
        .focus-ring:focus {
          outline: 2px solid #3b82f6;
          outline-offset: 2px;
          box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.1);
        }

        .dark .focus-ring:focus {
          outline-color: #60a5fa;
          box-shadow: 0 0 0 4px rgba(96, 165, 250, 0.1);
        }

        /* Enhanced hover effects */
        .sidebar-hover-effect {
          transform: translateX(0);
          transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
        }
        
        .sidebar-hover-effect:hover {
          transform: translateX(4px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }

        .dark .sidebar-hover-effect:hover {
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
        }

        /* Button hover animation */
        .button-hover-effect {
          position: relative;
          overflow: hidden;
          transform: scale(1);
          transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .button-hover-effect:hover {
          transform: scale(1.02);
          box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
        }

        .dark .button-hover-effect:hover {
          box-shadow: 0 6px 20px rgba(0, 0, 0, 0.4);
        }

        .button-hover-effect::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.1), transparent);
          transition: left 0.6s;
        }

        .button-hover-effect:hover::before {
          left: 100%;
        }

        /* Focus visible for keyboard navigation */
        .focus-visible-ring:focus-visible {
          outline: 3px solid #3b82f6;
          outline-offset: 2px;
          box-shadow: 0 0 0 6px rgba(59, 130, 246, 0.1);
          animation: focusPulse 2s infinite;
        }

        .dark .focus-visible-ring:focus-visible {
          outline-color: #60a5fa;
          box-shadow: 0 0 0 6px rgba(96, 165, 250, 0.1);
        }

        /* Pulse animation for focus states */
        @keyframes focusPulse {
          0%, 100% { box-shadow: 0 0 0 6px rgba(59, 130, 246, 0.1); }
          50% { box-shadow: 0 0 0 8px rgba(59, 130, 246, 0.15); }
        }

        /* Subtle breathing effect for active links */
        .sidebar-link-active {
          animation: activeGlow 3s ease-in-out infinite alternate;
        }

        @keyframes activeGlow {
          0% { box-shadow: 0 0 20px rgba(59, 130, 246, 0.3); }
          100% { box-shadow: 0 0 25px rgba(59, 130, 246, 0.4); }
        }

        .dark .sidebar-link-active {
          animation: activeGlowDark 3s ease-in-out infinite alternate;
        }

        @keyframes activeGlowDark {
          0% { box-shadow: 0 0 20px rgba(96, 165, 250, 0.2); }
          100% { box-shadow: 0 0 25px rgba(96, 165, 250, 0.3); }
        }
      `}</style>      {/* SIDEBAR - Enhanced with better visual hierarchy */}
      <aside
        className={`${darkMode ? 'bg-gradient-to-b from-slate-900 via-blue-900 to-slate-950' : 'bg-gradient-to-b from-blue-800 to-blue-900'} text-white w-64 sidebar-scrollbar
          fixed top-0 left-0 h-screen
          transform transition-all duration-300 ease-in-out
          overflow-y-auto z-30 shadow-2xl
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
          ${sidebarHidden ? 'md:-translate-x-full' : 'md:translate-x-0'}
          pt-12 pb-4 px-3
        `}
        style={{
          position: 'fixed',
          height: '100vh',
          overflowY: 'auto',
          scrollbarWidth: 'thin',
          scrollbarColor: '#2563eb #1e3a8a'
        }}
      >
        {/* Mobile close button */}
        <button
          onClick={() => setSidebarOpen(false)}
          className="absolute top-4 right-4 text-white md:hidden"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path 
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>

        {/* Title with enhanced styling */}
        <div className="mb-2">
          <h2 className="text-2xl font-bold text-center text-white drop-shadow-md">{title}</h2>
          <div className={`h-1 w-16 ${darkMode ? 'bg-blue-400' : 'bg-blue-400'} mx-auto mt-2 rounded-full`}></div>
        </div>

        {/* Navigation with enhanced active states */}
        <nav className="mt-8 space-y-2">
          {sidebarLinks.map((link, idx) => {
            const isActive = location.pathname === link.to;
            return (
              <Link
                key={idx}
                to={link.to}
                onClick={(e) => {
                  setSidebarOpen(false);
                  e.target.blur(); // Remove focus to clear hover state
                }}
                className={`flex items-center px-4 py-3 rounded-lg transition-all duration-200 group relative focus-visible-ring sidebar-hover-effect
                  ${isActive
                    ? `${darkMode ? 'bg-slate-800 shadow-blue-500/20 border border-blue-500/30' : 'bg-blue-600 shadow-blue-600/30'} text-white font-semibold shadow-lg sidebar-link-active` 
                    : `${darkMode ? 'hover:bg-slate-800/70 hover:shadow-blue-500/20 focus:bg-slate-800/70 focus:shadow-blue-500/20' : 'hover:bg-blue-700 hover:shadow-blue-600/30 focus:bg-blue-700 focus:shadow-blue-600/30'} hover:shadow-lg focus:shadow-lg active:bg-blue-800`
                  }`}
              >
                <link.icon className={`mr-3 transition-all duration-200 ${isActive ? 'scale-110 text-blue-200' : 'group-hover:scale-110 group-hover:text-blue-200 group-focus:scale-110 group-focus:text-blue-200'}`} />
                <span className={`flex-1 transition-all duration-200 ${isActive ? '' : 'group-hover:font-medium group-focus:font-medium'}`}>{link.label}</span>
                {link.shortcut && (
                  <kbd className={`hidden lg:inline-block px-2 py-0.5 text-xs ${darkMode ? 'bg-slate-950/70 border border-blue-800/30 group-hover:border-blue-600/50 group-focus:border-blue-600/50' : 'bg-blue-900 group-hover:bg-blue-800 group-focus:bg-blue-800'} bg-opacity-50 rounded transition-all duration-200 group-hover:scale-105 group-focus:scale-105`}>
                    {link.shortcut}
                  </kbd>
                )}
              </Link>
            );
          })}
          
          {/* Divider */}
          <div className={`pt-4 mt-4 border-t ${darkMode ? 'border-slate-700/50' : 'border-blue-700'}`}></div>
          
          {/* Profile Link - Only show if not SuperAdmin */}
          {showProfile && !isSuperAdmin && (
            <Link
              to={profilePath || "/employee/profile"}
              onClick={(e) => {
                setSidebarOpen(false);
                e.target.blur();
              }}
              className={`flex items-center px-4 py-3 ${darkMode ? 'hover:bg-slate-800/70 hover:shadow-blue-500/20 focus:bg-slate-800/70 focus:shadow-blue-500/20' : 'hover:bg-blue-700 hover:shadow-blue-600/30 focus:bg-blue-700 focus:shadow-blue-600/30'} focus:bg-blue-700 active:bg-blue-800 rounded-lg transition-all duration-200 hover:shadow-lg focus:shadow-lg focus-visible-ring sidebar-hover-effect group`}
            >
              <FaUser className="mr-3 transition-all duration-200 group-hover:scale-110 group-hover:text-blue-200 group-focus:scale-110 group-focus:text-blue-200" />
              <span className="flex-1 transition-all duration-200 group-hover:font-medium group-focus:font-medium">Profile</span>
              <kbd className={`hidden lg:inline-block px-2 py-0.5 text-xs ${darkMode ? 'bg-slate-950/70 border border-blue-800/30 group-hover:border-blue-600/50 group-focus:border-blue-600/50' : 'bg-blue-900 group-hover:bg-blue-800 group-focus:bg-blue-800'} bg-opacity-50 rounded transition-all duration-200 group-hover:scale-105 group-focus:scale-105`}>
                Ctrl+Shift+P
              </kbd>
            </Link>
          )}
          
          {/* Dark Mode Toggle */}
          <div
            onClick={(e) => {
              toggleDarkMode();
              e.target.blur();
            }}
            className={`flex items-center px-4 py-3 ${darkMode ? 'hover:bg-slate-800/70 hover:shadow-blue-500/20 focus:bg-slate-800/70 focus:shadow-blue-500/20' : 'hover:bg-blue-700 hover:shadow-blue-600/30 focus:bg-blue-700 focus:shadow-blue-600/30'} focus:bg-blue-700 active:bg-blue-800 rounded-lg cursor-pointer transition-all duration-200 hover:shadow-lg focus:shadow-lg focus-visible-ring sidebar-hover-effect group`}
            tabIndex={0}
            role="button"
            aria-label="Toggle dark mode"
          >
            {darkMode ? (
              <FaSun className="mr-3 transition-all duration-200 group-hover:scale-110 group-hover:rotate-90 group-hover:text-yellow-200 group-focus:scale-110 group-focus:rotate-90 group-focus:text-yellow-200" />
            ) : (
              <FaMoon className="mr-3 transition-all duration-200 group-hover:scale-110 group-hover:rotate-12 group-hover:text-blue-200 group-focus:scale-110 group-focus:rotate-12 group-focus:text-blue-200" />
            )}
            <span className="flex-1 transition-all duration-200 group-hover:font-medium group-focus:font-medium">{darkMode ? 'Light Mode' : 'Dark Mode'}</span>
            <kbd className={`hidden lg:inline-block px-2 py-0.5 text-xs ${darkMode ? 'bg-slate-950/70 border border-blue-800/30 group-hover:border-yellow-500/50 group-focus:border-yellow-500/50' : 'bg-blue-900 group-hover:bg-blue-800 group-focus:bg-blue-800'} bg-opacity-50 rounded transition-all duration-200 group-hover:scale-105 group-focus:scale-105`}>
              Ctrl+Shift+M
            </kbd>
          </div>
          
          {/* Logout with enhanced styling */}
          <div
            onClick={(e) => {
              onLogout();
              e.target.blur();
            }}
            className="flex items-center px-4 py-3 hover:bg-red-600 hover:shadow-red-600/30 focus:bg-red-600 focus:shadow-red-600/30 active:bg-red-700 rounded-lg cursor-pointer transition-all duration-200 hover:shadow-lg focus:shadow-lg focus-visible-ring sidebar-hover-effect group"
            tabIndex={0}
            role="button"
            aria-label="Logout"
          >
            <FaSignOutAlt className="mr-3 transition-all duration-200 group-hover:scale-110 group-hover:text-red-200 group-focus:scale-110 group-focus:text-red-200" />
            <span className="flex-1 transition-all duration-200 group-hover:font-medium group-focus:font-medium">Logout</span>
            <kbd className="hidden lg:inline-block px-2 py-0.5 text-xs bg-red-700 bg-opacity-50 rounded transition-all duration-200 group-hover:scale-105 group-focus:scale-105 group-hover:bg-red-600 group-focus:bg-red-600">
              Ctrl+L
            </kbd>
          </div>
        </nav>
      </aside>      {/* MAIN CONTENT - Enhanced responsive layout */}
      <div className={`min-h-screen layout-transition ${sidebarHidden ? '' : 'md:ml-64'}`}>
        {/* Mobile top bar with better styling */}
        {showTopBar && (
          <header className={`${darkMode ? 'bg-slate-900/95 backdrop-blur-sm text-white border-b border-slate-700/50' : 'bg-white'} shadow-md p-4 flex justify-between items-center md:hidden sticky top-0 z-20`}>
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className={`${darkMode ? 'text-slate-200 hover:text-white hover:bg-slate-800/50' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'} focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-lg p-3 transition-all duration-200 button-hover-effect focus-visible-ring`}
              aria-label="Toggle sidebar"
            >
              <FaBars size={20} className="transition-transform duration-200 hover:scale-110" />
            </button>
            <h1 className={`text-xl font-semibold ${darkMode ? 'text-slate-100' : 'text-gray-800'} truncate`}>{title}</h1>
            <div className="w-10"></div> {/* Spacer for centering */}
          </header>
        )}

        {/* Desktop top bar with enhanced controls */}
        {showTopBar && (
          <header className={`hidden md:flex ${darkMode ? 'bg-slate-900/95 backdrop-blur-sm text-white border-b border-slate-700/50' : 'bg-white'} shadow-sm p-4 justify-between items-center ${stickyTopBar ? 'sticky top-0 z-20' : ''}`}>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setSidebarHidden(!sidebarHidden)}
                className={`flex items-center space-x-2 px-4 py-2 ${darkMode ? 'text-slate-200 hover:text-white hover:bg-slate-800/70 hover:shadow-blue-500/20' : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100 hover:shadow-gray-300/50'} rounded-lg transition-all duration-200 focus-visible-ring shadow-sm hover:shadow-lg button-hover-effect`}
                title={sidebarHidden ? 'Show Sidebar' : 'Hide Sidebar'}
              >
                {sidebarHidden ? (
                  <>
                    <svg className="w-5 h-5 transition-transform duration-200 group-hover:scale-110" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
                    </svg>
                    <span className="text-sm font-medium transition-all duration-200 group-hover:font-semibold">Show Sidebar</span>
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5 transition-transform duration-200 group-hover:scale-110 group-hover:-translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                    </svg>
                    <span className="text-sm font-medium transition-all duration-200 group-hover:font-semibold">Hide Sidebar</span>
                  </>
                )}
              </button>
              <div className={`flex items-center gap-2 text-xs ${darkMode ? 'bg-slate-800/50 text-slate-300 border border-blue-800/30' : 'bg-gray-50 text-gray-500'} px-3 py-2 rounded-lg`}>
                <kbd className={`px-2 py-1 ${darkMode ? 'bg-slate-950/70 border-blue-800/30 text-blue-200 hover:border-blue-600/50' : 'bg-white border-gray-300 text-gray-700 hover:border-gray-400'} border rounded shadow-sm font-mono transition-all duration-200 hover:scale-105 hover:shadow-md`}>F11</kbd>
                <span>Full Screen</span>
              </div>
            </div>
            <div className={`text-lg font-semibold ${darkMode ? 'text-slate-100' : 'text-gray-800'} flex items-center gap-3`}>
              <span className="hidden lg:inline">{fullScreenMode ? `${title} - Full Screen` : title}</span>
              <button
                onClick={() => setFullScreenMode(!fullScreenMode)}
                className={`flex items-center space-x-2 px-4 py-2 ${darkMode ? 'text-slate-200 hover:text-white hover:bg-slate-800/70 hover:shadow-blue-500/20' : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100 hover:shadow-gray-300/50'} rounded-lg transition-all duration-200 text-sm shadow-sm hover:shadow-lg focus-visible-ring button-hover-effect group`}
                title={fullScreenMode ? 'Exit Full Screen (F11)' : 'Enter Full Screen (F11)'}
              >
                {fullScreenMode ? (
                  <>
                    <svg className="w-5 h-5 transition-all duration-200 group-hover:scale-110 group-hover:rotate-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 9V4.5M9 9H4.5M9 9L3.75 3.75M15 9V4.5M15 9h4.5M15 9l5.25-5.25M9 15v4.5M9 15H4.5M9 15l-5.25 5.25M15 15v4.5M15 15h4.5m0 0l5.25 5.25" />
                    </svg>
                    <span className="font-medium transition-all duration-200 group-hover:font-semibold">Exit Full Screen</span>
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5 transition-all duration-200 group-hover:scale-110 group-hover:rotate-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15" />
                    </svg>
                    <span className="font-medium transition-all duration-200 group-hover:font-semibold">Full Screen</span>
                  </>
                )}
              </button> 
            </div>
          </header>
        )}

        {/* Main content area with flexible sizing */}
        <main className={`${finalPadding} ${contentWidthClass} ${containerClass}`} style={contentStyle}>
          {children}
        </main>
      </div>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-20 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default DashboardLayout;
